import { motion } from "framer-motion";
import { CalendarDays, ClipboardList, Globe2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  formatOverviewDayLabel,
  responsesRemaining,
} from "./feedbackOverviewThresholds";

const THEMES = {
  amber: {
    blobA: "bg-amber-400/30",
    blobB: "bg-orange-400/20",
    ring: "#f59e0b",
    ringTrack: "rgba(245, 158, 11, 0.15)",
    iconWrapDark: "bg-amber-500/20 text-amber-200 ring-amber-400/30",
    iconWrapLight: "bg-amber-100 text-amber-700 ring-amber-200/80",
    badgeDark: "bg-amber-500/20 text-amber-200",
    badgeLight: "bg-amber-100 text-amber-800",
    btnHoverDark: "hover:border-amber-400/40 hover:bg-amber-500/10",
    btnHoverLight: "hover:border-amber-300 hover:bg-amber-50",
    panelDark: "border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-gray-900/40 to-orange-500/5",
    panelLight: "border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50/60",
  },
  sky: {
    blobA: "bg-sky-400/30",
    blobB: "bg-cyan-400/20",
    ring: "#0ea5e9",
    ringTrack: "rgba(14, 165, 233, 0.15)",
    iconWrapDark: "bg-sky-500/20 text-sky-200 ring-sky-400/30",
    iconWrapLight: "bg-sky-100 text-sky-700 ring-sky-200/80",
    badgeDark: "bg-sky-500/20 text-sky-200",
    badgeLight: "bg-sky-100 text-sky-800",
    btnHoverDark: "hover:border-sky-400/40 hover:bg-sky-500/10",
    btnHoverLight: "hover:border-sky-300 hover:bg-sky-50",
    panelDark: "border-sky-500/20 bg-gradient-to-br from-sky-500/10 via-gray-900/40 to-cyan-500/5",
    panelLight: "border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-cyan-50/60",
  },
};

function ProgressRing({ progress, color, trackColor, size = 88, stroke = 7 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - Math.min(1, Math.max(0, progress)) * circumference;

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.1, ease: "easeOut" }}
      />
    </svg>
  );
}

export function AtRiskPartialOverviewBanner({
  isDarkMode = false,
  scopedCount = 0,
  globalCount = 0,
  onSwitchToGlobal,
  variant = "amber",
}) {
  const { t } = useTranslation();
  const theme = THEMES[variant] || THEMES.amber;

  if (typeof onSwitchToGlobal !== "function") return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
        isDarkMode
          ? "border-amber-500/25 bg-amber-500/10 text-amber-100"
          : "border-amber-200 bg-amber-50 text-amber-900"
      }`}
    >
      <p className="text-sm leading-relaxed">
        {t("feedback.at_risk_partial_banner", {
          defaultValue:
            "Overview is limited to at-risk players ({{scoped}} response(s)). The full squad has {{global}} responses — switch to global mode to view all available indicators.",
          scoped: scopedCount,
          global: globalCount,
        })}
      </p>
      <button
        type="button"
        onClick={onSwitchToGlobal}
        className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
          isDarkMode
            ? `${theme.badgeDark} ${theme.btnHoverDark} border border-amber-400/30`
            : `${theme.badgeLight} ${theme.btnHoverLight} border border-amber-200`
        }`}
      >
        <Globe2 className="h-3.5 w-3.5" />
        {t("feedback.at_risk_switch_to_global", { defaultValue: "Switch to global mode" })}
      </button>
    </motion.div>
  );
}

export default function FeedbackInsufficientDataPanel({
  isDarkMode = false,
  answerCount = 0,
  minRequired = 5,
  previousDates = [],
  onSelectDate,
  layout = "hero",
  variant = "amber",
  title,
  message,
  hideProgress = false,
  onSwitchToGlobal,
}) {
  const { t, i18n } = useTranslation();
  const theme = THEMES[variant] || THEMES.amber;
  const remaining = responsesRemaining(answerCount, minRequired);
  const progress = minRequired > 0 ? answerCount / minRequired : 0;
  const isCompact = layout === "compact";
  const isEmpty = answerCount === 0;

  const panelTitle =
    title ||
    t("feedback.insufficient_data_title", {
      defaultValue: "Questionnaires not yet completed",
    });

  const panelMessage =
    message ||
    (isEmpty
      ? t("feedback.insufficient_data_empty", {
          defaultValue: "No players have answered the questionnaire yet.",
        })
      : t("feedback.insufficient_data_partial", {
          defaultValue: "Not enough responses to display reliable indicators.",
        }));

  return (
    <motion.div
      initial={{ opacity: 0, y: isCompact ? 8 : 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className={`relative overflow-hidden rounded-2xl border ${
        isCompact ? "p-4" : "p-6 sm:p-8"
      } ${isDarkMode ? theme.panelDark : theme.panelLight}`}
    >
      <motion.div
        className={`pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full blur-3xl ${theme.blobA}`}
        animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0.7, 0.45] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className={`pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full blur-3xl ${theme.blobB}`}
        animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.55, 0.35] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
      />

      <div className={`relative flex ${isCompact ? "flex-col gap-4" : "flex-col items-center gap-6 sm:flex-row sm:items-start"}`}>
        {!hideProgress && (
          <div className={`relative shrink-0 ${isCompact ? "mx-auto" : ""}`}>
            <ProgressRing
              progress={progress}
              color={theme.ring}
              trackColor={theme.ringTrack}
              size={isCompact ? 72 : 96}
              stroke={isCompact ? 6 : 7}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.35 }}
                className={`font-bold tabular-nums ${isCompact ? "text-lg" : "text-xl"} ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {answerCount}
              </motion.span>
              <span className={`text-[10px] uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                / {minRequired}
              </span>
            </div>
          </div>
        )}

        {hideProgress && (
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            className={`mx-auto flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ring-1 ${
              isDarkMode ? theme.iconWrapDark : theme.iconWrapLight
            }`}
          >
            <ClipboardList className="h-7 w-7" strokeWidth={1.75} />
          </motion.div>
        )}

        <div className={`min-w-0 flex-1 ${!isCompact && !hideProgress ? "sm:pt-1" : ""} ${hideProgress && !isCompact ? "text-center sm:text-left" : ""}`}>
          <div className={`flex items-start gap-2 ${hideProgress && !isCompact ? "justify-center sm:justify-start" : ""}`}>
            {!hideProgress && isCompact && (
              <motion.div
                animate={{ rotate: [0, 8, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${
                  isDarkMode ? theme.iconWrapDark : theme.iconWrapLight
                }`}
              >
                <Sparkles className="h-4 w-4" />
              </motion.div>
            )}
            <div>
              <motion.h4
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.35 }}
                className={`font-semibold ${isCompact ? "text-sm" : "text-lg sm:text-xl"}`}
              >
                {panelTitle}
              </motion.h4>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className={`mt-1.5 ${isCompact ? "text-xs" : "text-sm"} leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {panelMessage}
              </motion.p>
            </div>
          </div>

          {!hideProgress && remaining > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.35 }}
              className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
                isDarkMode ? theme.badgeDark : theme.badgeLight
              }`}
            >
              <motion.span
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block h-1.5 w-1.5 rounded-full bg-current"
              />
              {t("feedback.insufficient_data_remaining", {
                defaultValue: "{{count}} more needed",
                count: remaining,
              })}
            </motion.div>
          )}

          {previousDates.length > 0 && typeof onSelectDate === "function" ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="mt-5"
            >
              <p className={`mb-2.5 flex items-center gap-1.5 text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                <CalendarDays className="h-3.5 w-3.5" />
                {t("feedback.insufficient_data_previous_days", {
                  defaultValue: "View indicators from previous days",
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                {previousDates.map((entry, index) => (
                  <motion.button
                    key={entry.date}
                    type="button"
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.06, duration: 0.3 }}
                    whileHover={{ scale: 1.04, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectDate(entry.date)}
                    className={`rounded-xl border px-3 py-2 text-xs font-medium shadow-sm transition-colors ${
                      isDarkMode
                        ? `border-gray-600/80 bg-gray-900/60 text-gray-200 ${theme.btnHoverDark}`
                        : `border-gray-200/90 bg-white/90 text-gray-700 ${theme.btnHoverLight}`
                    }`}
                  >
                    {formatOverviewDayLabel(entry.date, i18n?.language || "fr-FR")}
                    <span className={`ml-1.5 tabular-nums ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                      · {entry.count}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : null}

          {typeof onSwitchToGlobal === "function" ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.35 }}
              className="mt-5"
            >
              <button
                type="button"
                onClick={onSwitchToGlobal}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
                  isDarkMode
                    ? `border-gray-600/80 bg-gray-900/60 text-gray-100 ${theme.btnHoverDark}`
                    : `border-gray-200/90 bg-white/90 text-gray-800 ${theme.btnHoverLight}`
                }`}
              >
                <Globe2 className="h-4 w-4" />
                {t("feedback.at_risk_switch_to_global", { defaultValue: "Switch to global mode" })}
              </button>
            </motion.div>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
